import { useEffect, useState } from 'react';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef, type CatalogApi } from '@backstage/plugin-catalog-react';
import type { Entity } from '@backstage/catalog-model';

// -----------------------------------------------------------------------------------
// Returns only the catalog `Application` entities related to the current user
// (OpenFGA `can_list` relation: business_manager, it_owner, a role in a project
// linked to the app, or manager of their division — see openfga/model/model.fga).
//
// Exception: `platform_admin` users and members of the `oficina-devops` group see
// all applications unfiltered, same as the rest of the administrative screens.
//
// Extracted from the logic already present in ApplicationPage.tsx ("Mis Aplicaciones")
// so it can also be reused in the golden path's custom field (OrgDataIaCField).
// -----------------------------------------------------------------------------------

export interface UseUserApplicationsResult {
  applications: Entity[];
  loading: boolean;
  error?: Error;
}

const DEVOPS_GROUP_OBJECT = 'group:oficina-devops';

async function resolveEffectiveUserEmail(
  identityApi: { getBackstageIdentity: () => Promise<{ userEntityRef: string }> },
  catalogApi: Pick<CatalogApi, 'getEntityByRef'>,
  configApi: { getOptionalString: (key: string) => string | undefined },
): Promise<string> {
  const identity = await identityApi.getBackstageIdentity();
  const userEntityRef = identity.userEntityRef;
  let userEmail = userEntityRef.split('/')[1];

  try {
    const userEntity = await catalogApi.getEntityByRef(userEntityRef);
    const profile = userEntity?.spec?.profile as { email?: string } | undefined;
    if (profile?.email) userEmail = profile.email;
  } catch {
    // No user entity in the catalog, fall back to the username.
  }

  const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
  const impersonatedFromConfig = configApi.getOptionalString('openfga.impersonatedUser');
  const impersonatedUser = impersonatedFromStorage || impersonatedFromConfig;

  if (!impersonatedUser || impersonatedUser === userEmail) {
    return userEmail;
  }

  try {
    const impersonatedUserEntity = await catalogApi.getEntityByRef(`user:default/${impersonatedUser}`);
    const impersonatedProfile = impersonatedUserEntity?.spec?.profile as { email?: string } | undefined;
    return impersonatedProfile?.email ?? impersonatedUser;
  } catch {
    return impersonatedUser;
  }
}

async function fetchIsPlatformAdmin(backendUrl: string, token: string, effectiveUser: string): Promise<boolean> {
  const response = await fetch(`${backendUrl}/api/openfga-client/check-permission`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      user: `user:${effectiveUser}`,
      action: 'platform_admin',
      resource: 'platform:main',
    }),
  });
  if (!response.ok) throw new Error(`check-permission failed: ${response.statusText}`);
  const data = await response.json();
  return Boolean(data.allowed);
}

async function fetchIsDevopsGroupMember(backendUrl: string, token: string, effectiveUser: string): Promise<boolean> {
  const objects = await fetchUserEntities(backendUrl, token, effectiveUser, 'group', 'member');
  return objects.includes(DEVOPS_GROUP_OBJECT);
}

async function fetchAllowedApplicationCodes(backendUrl: string, token: string, effectiveUser: string): Promise<string[]> {
  const objects = await fetchUserEntities(backendUrl, token, effectiveUser, 'application', 'can_list');
  return objects
    .map(ref => ref.match(/^application:(.+)$/i)?.[1])
    .filter((code): code is string => Boolean(code))
    .map(code => code.toUpperCase());
}

// `relation` is required and explicit on purpose: the backend (openfga-client-backend)
// defaults to `can_list` when omitted, and that relation doesn't exist on every type
// (e.g. `group` only has `member`), which makes OpenFGA respond with a 400.
async function fetchUserEntities(
  backendUrl: string,
  token: string,
  effectiveUser: string,
  type: string,
  relation: string,
): Promise<string[]> {
  const response = await fetch(`${backendUrl}/api/openfga-client/list-user-entities`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ user: `user:${effectiveUser}`, type, relation }),
  });
  if (!response.ok) throw new Error(`list-user-entities(${type}) failed: ${response.statusText}`);
  const data = await response.json();
  return data.objects ?? [];
}

export function useUserApplications(): UseUserApplicationsResult {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [applications, setApplications] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const backendUrl = configApi.getString('backend.baseUrl');
        const token = (await identityApi.getCredentials()).token ?? '';
        const effectiveUser = await resolveEffectiveUserEmail(identityApi, catalogApi, configApi);

        const [isPlatformAdmin, isDevopsGroupMember] = await Promise.all([
          fetchIsPlatformAdmin(backendUrl, token, effectiveUser),
          fetchIsDevopsGroupMember(backendUrl, token, effectiveUser),
        ]);
        const hasFullAccess = isPlatformAdmin || isDevopsGroupMember;

        const allowedCodes = hasFullAccess
          ? undefined
          : await fetchAllowedApplicationCodes(backendUrl, token, effectiveUser);

        const { items } = await catalogApi.getEntities({ filter: { kind: 'Application' } });
        const result = hasFullAccess
          ? items
          : items.filter(entity => allowedCodes!.includes(String(entity.spec?.number ?? '').toUpperCase()));

        if (!cancelled) {
          setApplications(result);
        }
      } catch (err) {
        if (!cancelled) {
          setApplications([]);
          setError(err as Error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [identityApi, configApi, catalogApi]);

  return { applications, loading, error };
}
